"use client";

import "@/lib/zod-locale";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import {
  isServer,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { useAttribution } from "@/hooks/use-attribution";
import {
  initFacebookPixel,
  isPixelPageViewPath,
  trackPageView,
} from "@/lib/facebook-pixel";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

function AttributionCapture({ children }: { children: React.ReactNode }) {
  useAttribution();
  return <>{children}</>;
}

const POSTHOG_API_HOST =
  process.env.NODE_ENV === "development"
    ? process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com"
    : "/ingest";

export default function Providers({ children }: { children: React.ReactNode }) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();
  const pathname = usePathname();

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !process.env.NEXT_PUBLIC_POSTHOG_KEY ||
      posthog.__loaded
    ) {
      return;
    }

    const initPosthog = () => {
      if (posthog.__loaded) return;
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
        api_host: POSTHOG_API_HOST,
        ui_host: "https://us.posthog.com",
        person_profiles: "identified_only",
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: {
          element_allowlist: [
            "a",
            "button",
            "form",
            "input",
            "select",
            "textarea",
            "label",
          ],
        },
        capture_exceptions: true,
        capture_performance: { web_vitals: false },
        disable_session_recording: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "[data-ph-mask]",
        },
        before_send: (event) => {
          if (!event) return event;
          if (event.event === "$exception") {
            const exceptions =
              (event.properties?.$exception_list as
                | Array<{ value?: string; type?: string }>
                | undefined) ?? [];
            const primaryException = exceptions[0];
            const legacyMessage =
              (event.properties?.$exception_message as string | undefined) ??
              "";
            const messages = [
              legacyMessage,
              ...exceptions.map((e) => e?.value ?? ""),
            ];
            const primaryMessage = primaryException?.value ?? legacyMessage;
            const primaryType = primaryException?.type ?? "";

            const isNoise = messages.some((m) => {
              const msg = m.toLowerCase();
              return (
                m.includes("__SCRIPT_SOURCE_PLACEHOLDER__") ||
                msg.includes("window.webkit.messagehandlers") ||
                msg.startsWith("resizeobserver loop") ||
                msg.includes("non-error promise rejection captured")
              );
            });
            const isBenignScriptError =
              primaryType === "ScriptError" ||
              primaryMessage === "Script error.";

            if (isNoise || isBenignScriptError) return null;
          }
          return event;
        },
        loaded: (posthog) => {
          if (process.env.NODE_ENV === "development") posthog.debug();
        },
      });
    };

    // Defer PostHog init until after hydration to avoid blocking paint.
    // Use requestIdleCallback when available, with a setTimeout fallback
    // for browsers (e.g. Safari) that don't support it.
    const w = window as Window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout?: number },
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    if (typeof w.requestIdleCallback === "function") {
      idleHandle = w.requestIdleCallback(initPosthog, { timeout: 2000 });
    } else {
      timeoutHandle = setTimeout(initPosthog, 1);
    }

    return () => {
      if (idleHandle !== undefined && typeof w.cancelIdleCallback === "function") {
        w.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
      }
    };
  }, []);

  useEffect(() => {
    if (!isPixelPageViewPath(pathname)) {
      return;
    }

    void (async () => {
      await initFacebookPixel();
      await trackPageView();
    })();
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <PostHogProvider client={posthog}>
        <AttributionCapture>{children}</AttributionCapture>
      </PostHogProvider>
    </QueryClientProvider>
  );
}
